import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../../assets/theme/custom/hooks";
import { AppSlideOver } from "../../../../components/layout";
import {
  AppButton,
  AppAutocomplete,
  AppCheckbox,
  AppTextField,
  AppToggleField,
} from "../../../../components/form";
import JsonCodeEditor from "../../../../components/JsonCodeEditor";
import OpenSearchIndexPicker from "../../../../components/OpenSearchIndexPicker";
import { prepService } from "../../../../services/prepService";
import { workspaceService } from "../../../../services/workspaceService";
import {
  openapiCatalogService,
  type OpenApiCatalogSummary,
  type OpenApiTagSummary,
} from "../../../../services/openapiCatalogService";
import type { DataAdaptorSummary } from "../../../../services/adminDataAdaptorService";
import type { PrepEdgeFieldOptionsResponse, PrepGraphEdge, PrepGraphNode, PrepJobGraph, WatermarkKind } from "../../../../types/prep";
import { upstreamFieldCatalog, edgeFieldPathsFromNode, applyGraphEdgePatch, applyGraphNodePatch, isPrepSourceType } from "../../utils/graphAdapter";
import { edgeFieldMappingCompatible } from "../../utils/prepFieldMapping";
import PrepDslSimpleBuilder from "../PrepDslSimpleBuilder";
import PrepEdgeConfigForm from "./PrepEdgeConfigForm";
import PrepScheduleCronField from "../PrepScheduleCronField";

const DRAWER_WIDTH = 480;

export type PrepDrawerSelection =
  | { kind: "node"; node: PrepGraphNode }
  | { kind: "edge"; edge: PrepGraphEdge; sourceNode?: PrepGraphNode; targetNode?: PrepGraphNode };

type Props = {
  open: boolean;
  selection: PrepDrawerSelection | null;
  graph?: PrepJobGraph | null;
  jobId?: string | null;
  onClose: () => void;
  onSaveNode: (node: PrepGraphNode) => void;
  onSaveEdge: (edge: PrepGraphEdge) => void;
  onDeleteSelection?: () => void;
  canDeleteSelection?: boolean;
  onTestNode?: (nodeId: string, nodeSnapshot: PrepGraphNode) => void;
  onViewRuns?: () => void;
  testing?: boolean;
  onRegisterDraftApplier?: (applier: ((graph: PrepJobGraph) => PrepJobGraph) | null) => void;
};

export default function PrepConfigDrawer({
  open,
  selection,
  graph,
  jobId,
  onClose,
  onSaveNode,
  onSaveEdge,
  onDeleteSelection,
  canDeleteSelection = false,
  onTestNode,
  onViewRuns,
  testing,
  onRegisterDraftApplier,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const [draftNode, setDraftNode] = useState<PrepGraphNode | null>(null);
  const [draftEdge, setDraftEdge] = useState<PrepGraphEdge | null>(null);
  const [dslJson, setDslJson] = useState("{}");
  const [mongoJson, setMongoJson] = useState("{}");
  const [openApiArgsJson, setOpenApiArgsJson] = useState("{}");
  const [adaptors, setAdaptors] = useState<DataAdaptorSummary[]>([]);
  const [openApiCatalogs, setOpenApiCatalogs] = useState<OpenApiCatalogSummary[]>([]);
  const [openApiTags, setOpenApiTags] = useState<OpenApiTagSummary[]>([]);
  const [openApiTools, setOpenApiTools] = useState<
    Array<{ toolName: string; method?: string; path?: string; summary?: string }>
  >([]);
  const [edgeOptions, setEdgeOptions] = useState<PrepEdgeFieldOptionsResponse | null>(null);
  const [resettingCursor, setResettingCursor] = useState(false);
  const [beasIndexPrefix, setBeasIndexPrefix] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    workspaceService
      .getSettings()
      .then((s) => {
        if (s.openSearchMode === "BEAS_MANAGED" && s.openSearchIndexPrefix) {
          setBeasIndexPrefix(s.openSearchIndexPrefix);
        } else {
          setBeasIndexPrefix(null);
        }
      })
      .catch(() => setBeasIndexPrefix(null));
  }, [open]);

  useEffect(() => {
    if (selection?.kind === "node") {
      setDraftNode({ ...selection.node });
      setDraftEdge(null);
      setEdgeOptions(null);
      setDslJson(JSON.stringify(selection.node.dslTemplate ?? { size: 2, query: { match_all: {} } }, null, 2));
      setMongoJson(JSON.stringify(selection.node.mongoCommand ?? { find: "collection", filter: {} }, null, 2));
      setOpenApiArgsJson(JSON.stringify(selection.node.openApiPathArgs ?? {}, null, 2));
    } else if (selection?.kind === "edge") {
      setDraftEdge({ ...selection.edge });
      setDraftNode(null);
    } else {
      setDraftNode(null);
      setDraftEdge(null);
      setEdgeOptions(null);
    }
  }, [selection]);

  useEffect(() => {
    if (!open) return;
    prepService.listAdaptors().then(setAdaptors).catch(() => setAdaptors([]));
    openapiCatalogService.listChatCatalogs().then(setOpenApiCatalogs).catch(() => setOpenApiCatalogs([]));
  }, [open]);

  useEffect(() => {
    const catalogId = draftNode?.type === "OPENAPI_QUERY" ? draftNode.openApiCatalogId : undefined;
    if (!catalogId) {
      setOpenApiTags([]);
      return;
    }
    openapiCatalogService
      .listTags(catalogId)
      .then(setOpenApiTags)
      .catch(() => setOpenApiTags([]));
  }, [draftNode?.type, draftNode?.openApiCatalogId]);

  useEffect(() => {
    const catalogId = draftNode?.type === "OPENAPI_QUERY" ? draftNode.openApiCatalogId : undefined;
    const tag = draftNode?.type === "OPENAPI_QUERY" ? draftNode.openApiTag : undefined;
    if (!catalogId || !tag) {
      setOpenApiTools([]);
      return;
    }
    openapiCatalogService
      .listToolsForTag(catalogId, tag)
      .then((tools) => setOpenApiTools(tools.filter((t) => t.write !== true)))
      .catch(() => setOpenApiTools([]));
  }, [draftNode?.type, draftNode?.openApiCatalogId, draftNode?.openApiTag]);

  useEffect(() => {
    if (adaptors.length === 0) {
      return;
    }
    setDraftNode((current) => {
      if (!current?.adaptorId || current.adaptorType) {
        return current;
      }
      const matched = adaptors.find((item) => item.id === current.adaptorId);
      return matched ? { ...current, adaptorType: matched.type } : current;
    });
  }, [adaptors]);

  const liveEdgeNodes = useMemo(() => {
    if (selection?.kind !== "edge" || !graph) {
      return { source: undefined as PrepGraphNode | undefined, target: undefined as PrepGraphNode | undefined };
    }
    const { sourceNodeId, targetNodeId } = selection.edge;
    return {
      source: graph.nodes.find((n) => n.id === sourceNodeId),
      target: graph.nodes.find((n) => n.id === targetNodeId),
    };
  }, [selection, graph]);

  const edgeFieldsRevision = useMemo(() => {
    const { source, target } = liveEdgeNodes;
    return [
      source?.lastTest?.testedAt ?? "",
      source?.lastTest?.graphFingerprint ?? "",
      target?.lastTest?.testedAt ?? "",
      target?.lastTest?.graphFingerprint ?? "",
    ].join("|");
  }, [liveEdgeNodes]);

  useEffect(() => {
    if (selection?.kind !== "edge" || !jobId || !selection.edge.id) {
      setEdgeOptions(null);
      return;
    }
    prepService
      .getEdgeFieldOptions(jobId, selection.edge.id)
      .then(setEdgeOptions)
      .catch(() => setEdgeOptions(null));
  }, [selection, jobId, edgeFieldsRevision]);

  const upstreamFields = useMemo(
    () => edgeFieldPathsFromNode(liveEdgeNodes.source, "upstream"),
    [liveEdgeNodes.source]
  );

  const downstreamFields = useMemo(
    () => edgeFieldPathsFromNode(liveEdgeNodes.target, "downstream"),
    [liveEdgeNodes.target]
  );

  const upstreamTested = Boolean(
    liveEdgeNodes.source?.lastTest && !liveEdgeNodes.source.lastTest.errorMessage
  );
  const downstreamTested =
    liveEdgeNodes.target?.type === "OUTPUT" ||
    Boolean(liveEdgeNodes.target?.lastTest && !liveEdgeNodes.target.lastTest.errorMessage);

  const edgeFieldMappingValid = useMemo(() => {
    if (!draftEdge || liveEdgeNodes.target?.type === "OUTPUT") {
      return true;
    }
    return edgeFieldMappingCompatible(
      liveEdgeNodes.source?.lastTest?.sampleRows,
      draftEdge.upstreamField,
      liveEdgeNodes.target?.lastTest?.sampleRows,
      draftEdge.downstreamField
    );
  }, [draftEdge, liveEdgeNodes.source, liveEdgeNodes.target]);

  const buildNodeSnapshot = (): PrepGraphNode | null => {
    if (!draftNode) return null;
    if (draftNode.type === "DSL_QUERY") {
      let dsl: Record<string, unknown> = draftNode.dslTemplate ?? {};
      try {
        dsl = JSON.parse(dslJson) as Record<string, unknown>;
      } catch {
        /* keep previous */
      }
      return { ...draftNode, dslTemplate: dsl };
    }
    if (draftNode.type === "ADAPTOR_QUERY") {
      let mongoCommand: Record<string, unknown> = draftNode.mongoCommand ?? {};
      try {
        mongoCommand = JSON.parse(mongoJson) as Record<string, unknown>;
      } catch {
        /* keep previous */
      }
      return { ...draftNode, mongoCommand };
    }
    if (draftNode.type === "OPENAPI_QUERY") {
      let openApiPathArgs: Record<string, unknown> = draftNode.openApiPathArgs ?? {};
      try {
        openApiPathArgs = JSON.parse(openApiArgsJson) as Record<string, unknown>;
      } catch {
        /* keep previous */
      }
      return { ...draftNode, openApiPathArgs };
    }
    return { ...draftNode };
  };

  useEffect(() => {
    if (!open || !onRegisterDraftApplier) {
      onRegisterDraftApplier?.(null);
      return;
    }
    onRegisterDraftApplier((graph) => {
      if (draftNode) {
        const snapshot = buildNodeSnapshot();
        return snapshot ? applyGraphNodePatch(graph, snapshot) : graph;
      }
      if (draftEdge) {
        return applyGraphEdgePatch(graph, draftEdge);
      }
      return graph;
    });
    return () => onRegisterDraftApplier(null);
  }, [open, draftNode, draftEdge, dslJson, mongoJson, openApiArgsJson, onRegisterDraftApplier]);

  const syncOutputDraft = () => {
    if (draftNode?.type !== "OUTPUT") return;
    onSaveNode({ ...draftNode });
  };

  const upstreamCatalog = useMemo(() => {
    if (selection?.kind !== "node" || !isPrepSourceType(selection.node.type) || !graph) {
      return [];
    }
    return upstreamFieldCatalog(graph, selection.node.id);
  }, [selection, graph]);

  const handleSave = () => {
    if (draftNode) {
      const snapshot = buildNodeSnapshot();
      if (snapshot) onSaveNode(snapshot);
    }
    if (draftEdge) {
      if (!edgeFieldMappingValid) {
        return;
      }
      onSaveEdge(draftEdge);
    }
    onClose();
  };

  const title = selection?.kind === "edge" ? t("prep.drawer.edgeTitle") : t("prep.drawer.nodeTitle");

  return (
    <AppSlideOver open={open} onClose={onClose} size="lg" title={title}>
      <Box sx={custom.components.dataPrep.drawerRoot}>
        <Box sx={custom.components.dataPrep.drawerBody}>
          {draftNode?.type === "DSL_QUERY" && (
            <Stack spacing={2}>
              <AppTextField
                label={t("prep.drawer.label")}
                value={draftNode.label ?? ""}
                onChange={(e) => setDraftNode({ ...draftNode, label: e.target.value })}
                fullWidth
              />
              <OpenSearchIndexPicker
                label={t("prep.drawer.sourceIndex")}
                value={draftNode.sourceIndex ? [draftNode.sourceIndex] : []}
                onChange={(indices) => setDraftNode({ ...draftNode, sourceIndex: indices[0] ?? "" })}
                searchEnabled={open}
                fullWidth
              />
              <AppTextField
                label={t("prep.drawer.bulkSize")}
                type="number"
                value={draftNode.bulkSize ?? 50}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setDraftNode({
                    ...draftNode,
                    bulkSize: Number.isFinite(value) && value >= 1 ? Math.floor(value) : 50,
                  });
                }}
                fullWidth
                inputProps={{ min: 1 }}
                helperText={t("prep.drawer.bulkSizeHelp")}
              />
              {upstreamCatalog.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    {t("prep.drawer.upstreamCatalog")}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {upstreamCatalog.map((f) => (
                      <Chip key={f.path} size="small" label={f.path} variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}
              <PrepDslSimpleBuilder
                sourceIndex={draftNode.sourceIndex}
                dslJson={dslJson}
                onDslChange={setDslJson}
              />
              <Typography variant="subtitle2" color="text.secondary">
                {t("prep.drawer.dsl")}
              </Typography>
              <JsonCodeEditor value={dslJson} onChange={setDslJson} height={220} />
              {onTestNode && (
                <AppButton
                  variant="outlined"
                  color="primary"
                  sx={custom.components.dataPrep.drawerFooterOutlinedPrimary}
                  onClick={() => {
                    const snapshot = buildNodeSnapshot();
                    if (!snapshot) return;
                    onSaveNode(snapshot);
                    onTestNode(snapshot.id, snapshot);
                  }}
                  disabled={testing}
                >
                  {t("prep.drawer.testNode")}
                </AppButton>
              )}
            </Stack>
          )}
          {draftNode?.type === "ADAPTOR_QUERY" && (
            <Stack spacing={2}>
              <AppTextField
                label={t("prep.drawer.label")}
                value={draftNode.label ?? ""}
                onChange={(e) => setDraftNode({ ...draftNode, label: e.target.value })}
                fullWidth
              />
              <AppAutocomplete
                label={t("prep.drawer.adaptor")}
                options={adaptors}
                value={adaptors.find((item) => item.id === draftNode.adaptorId) ?? null}
                getOptionLabel={(item) => `${item.name} (${item.type})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) =>
                  setDraftNode({
                    ...draftNode,
                    adaptorId: value?.id,
                    adaptorType: value?.type,
                  })
                }
              />
              <AppTextField
                label={t("prep.drawer.bulkSize")}
                type="number"
                value={draftNode.bulkSize ?? 50}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setDraftNode({
                    ...draftNode,
                    bulkSize: Number.isFinite(value) && value >= 1 ? Math.floor(value) : 50,
                  });
                }}
                fullWidth
                inputProps={{ min: 1 }}
                helperText={t("prep.drawer.adaptorBulkSizeHelp")}
              />
              <AppToggleField
                label={t("prep.drawer.watermarkKind")}
                value={draftNode.watermarkKind ?? "DOCUMENT_ORDER"}
                onChange={(v) => setDraftNode({ ...draftNode, watermarkKind: v as WatermarkKind })}
                options={[
                  { value: "DATE", label: t("prep.drawer.watermarkDate") },
                  { value: "NUMBER", label: t("prep.drawer.watermarkNumber") },
                  { value: "DOCUMENT_ORDER", label: t("prep.drawer.watermarkOrder") },
                ]}
              />
              {(draftNode.watermarkKind ?? "DOCUMENT_ORDER") !== "DOCUMENT_ORDER" && (
                <AppTextField
                  label={t("prep.drawer.watermarkField")}
                  value={draftNode.watermarkField ?? ""}
                  onChange={(e) => setDraftNode({ ...draftNode, watermarkField: e.target.value })}
                  fullWidth
                  helperText={t("prep.drawer.watermarkFieldHelp")}
                />
              )}
              {draftNode.watermarkKind === "DOCUMENT_ORDER" && (
                <Alert severity="info">{t("prep.drawer.watermarkOrderHelp")}</Alert>
              )}
              {draftNode.adaptorType === "MONGODB" ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("prep.drawer.mongoCommand")}
                  </Typography>
                  <JsonCodeEditor value={mongoJson} onChange={setMongoJson} height={220} />
                </>
              ) : (
                <AppTextField
                  label={t("prep.drawer.sqlQuery")}
                  value={draftNode.sqlQuery ?? ""}
                  onChange={(e) => setDraftNode({ ...draftNode, sqlQuery: e.target.value })}
                  fullWidth
                  multiline
                  minRows={6}
                  helperText={t("prep.drawer.sqlQueryHelp")}
                />
              )}
              {upstreamCatalog.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    {t("prep.drawer.upstreamCatalog")}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {upstreamCatalog.map((f) => (
                      <Chip key={f.path} size="small" label={f.path} variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}
              {onTestNode && (
                <AppButton
                  variant="outlined"
                  color="primary"
                  sx={custom.components.dataPrep.drawerFooterOutlinedPrimary}
                  onClick={() => {
                    const snapshot = buildNodeSnapshot();
                    if (!snapshot) return;
                    onSaveNode(snapshot);
                    onTestNode(snapshot.id, snapshot);
                  }}
                  disabled={testing}
                >
                  {t("prep.drawer.testNode")}
                </AppButton>
              )}
              {jobId && (
                <AppButton
                  variant="outlined"
                  color="primary"
                  sx={custom.components.dataPrep.drawerFooterOutlinedPrimary}
                  disabled={resettingCursor}
                  onClick={async () => {
                    setResettingCursor(true);
                    try {
                      await prepService.resetAdaptorCursor(jobId, draftNode.id);
                    } finally {
                      setResettingCursor(false);
                    }
                  }}
                >
                  {t("prep.drawer.resetCursor")}
                </AppButton>
              )}
            </Stack>
          )}
          {draftNode?.type === "OPENAPI_QUERY" && (
            <Stack spacing={2}>
              <AppTextField
                label={t("prep.drawer.label")}
                value={draftNode.label ?? ""}
                onChange={(e) => setDraftNode({ ...draftNode, label: e.target.value })}
                fullWidth
              />
              <AppAutocomplete
                label={t("prep.drawer.openApiCatalog")}
                options={openApiCatalogs}
                value={openApiCatalogs.find((item) => item.id === draftNode.openApiCatalogId) ?? null}
                getOptionLabel={(item) => item.name || item.id}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) =>
                  setDraftNode({
                    ...draftNode,
                    openApiCatalogId: value?.id,
                    openApiTag: undefined,
                    openApiToolName: undefined,
                  })
                }
              />
              <AppAutocomplete
                label={t("prep.drawer.openApiTag")}
                options={openApiTags}
                value={openApiTags.find((item) => item.tag === draftNode.openApiTag) ?? null}
                getOptionLabel={(item) =>
                  item.operationCount != null ? `${item.tag} (${item.operationCount})` : item.tag
                }
                isOptionEqualToValue={(a, b) => a.tag === b.tag}
                onChange={(_, value) =>
                  setDraftNode({
                    ...draftNode,
                    openApiTag: value?.tag,
                    openApiToolName: undefined,
                  })
                }
                disabled={!draftNode.openApiCatalogId}
              />
              <AppAutocomplete
                label={t("prep.drawer.openApiTool")}
                options={openApiTools}
                value={openApiTools.find((item) => item.toolName === draftNode.openApiToolName) ?? null}
                getOptionLabel={(item) =>
                  item.method && item.path
                    ? `${item.toolName} · ${item.method} ${item.path}`
                    : item.toolName
                }
                isOptionEqualToValue={(a, b) => a.toolName === b.toolName}
                onChange={(_, value) =>
                  setDraftNode({
                    ...draftNode,
                    openApiToolName: value?.toolName,
                  })
                }
                disabled={!draftNode.openApiTag}
              />
              <AppTextField
                label={t("prep.drawer.bulkSize")}
                type="number"
                value={draftNode.bulkSize ?? 50}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setDraftNode({
                    ...draftNode,
                    bulkSize: Number.isFinite(value) && value >= 1 ? Math.floor(value) : 50,
                  });
                }}
                fullWidth
                inputProps={{ min: 1 }}
                helperText={t("prep.drawer.openApiBulkSizeHelp")}
              />
              <AppTextField
                label={t("prep.drawer.openApiResponsePath")}
                value={draftNode.openApiResponseArrayPath ?? ""}
                onChange={(e) => setDraftNode({ ...draftNode, openApiResponseArrayPath: e.target.value })}
                fullWidth
                helperText={t("prep.drawer.openApiResponsePathHelp")}
              />
              <Typography variant="subtitle2" color="text.secondary">
                {t("prep.drawer.openApiPathArgs")}
              </Typography>
              <JsonCodeEditor value={openApiArgsJson} onChange={setOpenApiArgsJson} height={180} />
              {upstreamCatalog.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    {t("prep.drawer.upstreamCatalog")}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {upstreamCatalog.map((f) => (
                      <Chip key={f.path} size="small" label={f.path} variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              )}
              {onTestNode && (
                <AppButton
                  variant="outlined"
                  color="primary"
                  sx={custom.components.dataPrep.drawerFooterOutlinedPrimary}
                  onClick={() => {
                    const snapshot = buildNodeSnapshot();
                    if (!snapshot) return;
                    onSaveNode(snapshot);
                    onTestNode(snapshot.id, snapshot);
                  }}
                  disabled={testing}
                >
                  {t("prep.drawer.testNode")}
                </AppButton>
              )}
            </Stack>
          )}
          {draftNode?.type === "OUTPUT" && (
            <Stack spacing={2}>
              <AppToggleField
                label={t("prep.drawer.outputTargetKind")}
                value={(draftNode as { outputTargetKind?: string }).outputTargetKind ?? "OPENSEARCH"}
                onChange={(v) =>
                  setDraftNode({
                    ...draftNode,
                    outputTargetKind: v as "OPENSEARCH" | "ADAPTOR",
                  } as typeof draftNode)
                }
                options={[
                  { value: "OPENSEARCH", label: "OpenSearch" },
                  { value: "ADAPTOR", label: "Adaptor" },
                ]}
              />
              {((draftNode as { outputTargetKind?: string }).outputTargetKind ?? "OPENSEARCH") ===
              "ADAPTOR" ? (
                <>
                  <AppTextField
                    label={t("prep.drawer.outputAdaptorId")}
                    value={(draftNode as { adaptorId?: string }).adaptorId ?? ""}
                    onChange={(e) =>
                      setDraftNode({ ...draftNode, adaptorId: e.target.value } as typeof draftNode)
                    }
                    fullWidth
                  />
                  <AppTextField
                    label={t("prep.drawer.outputTargetTable")}
                    value={(draftNode as { outputTargetTable?: string }).outputTargetTable ?? ""}
                    onChange={(e) =>
                      setDraftNode({
                        ...draftNode,
                        outputTargetTable: e.target.value,
                      } as typeof draftNode)
                    }
                    fullWidth
                  />
                  <AppToggleField
                    label={t("prep.drawer.outputWriteMode")}
                    value={(draftNode as { outputWriteMode?: string }).outputWriteMode ?? "INSERT"}
                    onChange={(v) =>
                      setDraftNode({
                        ...draftNode,
                        outputWriteMode: v as "INSERT" | "UPSERT",
                      } as typeof draftNode)
                    }
                    options={[
                      { value: "INSERT", label: "INSERT" },
                      { value: "UPSERT", label: "UPSERT" },
                    ]}
                  />
                  <AppTextField
                    label={t("prep.drawer.outputKeyFields")}
                    value={((draftNode as { outputKeyFields?: string[] }).outputKeyFields || []).join(
                      ", "
                    )}
                    onChange={(e) =>
                      setDraftNode({
                        ...draftNode,
                        outputKeyFields: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      } as typeof draftNode)
                    }
                    fullWidth
                    helperText={t("prep.drawer.outputKeyFieldsHelp")}
                  />
                </>
              ) : (
              <AppTextField
                label={t("prep.drawer.targetIndex")}
                value={draftNode.targetIndex ?? ""}
                onChange={(e) => setDraftNode({ ...draftNode, targetIndex: e.target.value })}
                onBlur={syncOutputDraft}
                fullWidth
                helperText={
                  beasIndexPrefix
                    ? t("prep.drawer.targetIndexBeasHint", {
                        prefix: beasIndexPrefix,
                        full: `${beasIndexPrefix}${draftNode.targetIndex || "…"}`,
                      })
                    : undefined
                }
              />
              )}
              <AppTextField
                label={t("prep.drawer.indexBulkSize")}
                type="number"
                value={draftNode.bulkSize ?? 50}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setDraftNode({
                    ...draftNode,
                    bulkSize: Number.isFinite(value) && value >= 1 ? Math.floor(value) : 50,
                  });
                }}
                fullWidth
                inputProps={{ min: 1 }}
                helperText={t("prep.drawer.indexBulkSizeHelp")}
              />
              <Chip
                size="small"
                label={`${t("prep.drawer.approvalStatus")}: ${draftNode.indexApprovalStatus ?? "PENDING"}`}
                color={draftNode.indexApprovalStatus === "APPROVED" ? "success" : "default"}
              />
              <AppToggleField
                label={t("prep.drawer.environment")}
                value={draftNode.environment ?? "STAGING"}
                onChange={(v) =>
                  setDraftNode({ ...draftNode, environment: v as "STAGING" | "PRODUCTION" })
                }
                options={[
                  { value: "STAGING", label: "STAGING" },
                  { value: "PRODUCTION", label: "PRODUCTION" },
                ]}
              />
              <AppToggleField
                label={t("prep.drawer.refreshMode")}
                value={draftNode.refreshMode ?? "FULL"}
                onChange={(v) =>
                  setDraftNode({ ...draftNode, refreshMode: v as "FULL" | "INCREMENTAL" })
                }
                options={[
                  { value: "FULL", label: t("prep.drawer.refreshFull") },
                  { value: "INCREMENTAL", label: t("prep.drawer.refreshIncremental") },
                ]}
              />
              <AppCheckbox
                label={t("prep.drawer.piiMask")}
                checked={draftNode.piiMaskEnabled ?? true}
                onChange={(e) => setDraftNode({ ...draftNode, piiMaskEnabled: e.target.checked })}
              />
              <AppCheckbox
                label={t("prep.drawer.skipFailedRecords")}
                checked={draftNode.skipFailedRecords ?? false}
                onChange={(e) => setDraftNode({ ...draftNode, skipFailedRecords: e.target.checked })}
              />
              <AppCheckbox
                label={t("prep.drawer.duplicateRecords")}
                checked={draftNode.duplicateRecords ?? false}
                onChange={(e) => setDraftNode({ ...draftNode, duplicateRecords: e.target.checked })}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={custom.components.dataPrep.drawerHintCaption}>
                {t("prep.drawer.duplicateRecordsHint")}
              </Typography>
              <PrepScheduleCronField
                value={draftNode.scheduleCron}
                onChange={(scheduleCron) => setDraftNode({ ...draftNode, scheduleCron })}
              />
              {onViewRuns && jobId && (
                <AppButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={custom.components.dataPrep.drawerFooterOutlinedPrimary}
                  onClick={onViewRuns}
                >
                  {t("prep.drawer.viewRuns")}
                </AppButton>
              )}
              <Alert severity="info" sx={custom.pages.shared.alertCompactPy}>
                {t("prep.drawer.outputNotDeletable")}
              </Alert>
            </Stack>
          )}
          {draftEdge && selection?.kind === "edge" && (
            <PrepEdgeConfigForm
              draftEdge={draftEdge}
              sourceNode={liveEdgeNodes.source}
              targetNode={liveEdgeNodes.target}
              edgeSummary={edgeOptions?.summary}
              upstreamFields={upstreamFields}
              downstreamFields={downstreamFields}
              upstreamTested={upstreamTested}
              downstreamTested={downstreamTested}
              upstreamSampleRows={liveEdgeNodes.source?.lastTest?.sampleRows}
              downstreamSampleRows={liveEdgeNodes.target?.lastTest?.sampleRows}
              onChange={setDraftEdge}
            />
          )}
        </Box>

        <Divider />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={custom.components.dataPrep.drawerFooter}>
          <AppButton
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={Boolean(draftEdge) && !edgeFieldMappingValid}
          >
            {t("common.save")}
          </AppButton>
          <AppButton variant="outlined" color="primary" sx={custom.components.dataPrep.drawerFooterOutlinedPrimary} onClick={onClose}>
            {t("common.cancel")}
          </AppButton>
          {canDeleteSelection && onDeleteSelection && (
            <AppButton
              variant="outlined"
              color="error"
              sx={custom.components.dataPrep.drawerFooterOutlinedError}
              onClick={onDeleteSelection}
            >
              {selection?.kind === "edge" ? t("prep.drawer.deleteEdge") : t("prep.drawer.deleteNode")}
            </AppButton>
          )}
        </Stack>
      </Box>
    </AppSlideOver>
  );
}
